"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { Wind, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface TechHighlight {
  label: L6;
  val: string;
}
const technicalHighlights: TechHighlight[] = [{
  label: {
    en: "Operating Service",
    zh: "适用工况特性",
    es: "Servicio operativo",
    de: "Betriebseinsatz",
    fr: "Service de fonctionnement",
    pt: "Serviço operacional"
  },
  val: "High-Temperature Service"
}, {
  label: {
    en: "Micron Rating",
    zh: "气体微孔范围",
    es: "Grado de filtración",
    de: "Filterfeinheit",
    fr: "Seuil de filtration",
    pt: "Grau de filtração"
  },
  val: "1 µm – 250 µm"
}, {
  label: {
    en: "Alloy Selection",
    zh: "推荐合金牌号",
    es: "Selección de aleaciones",
    de: "Legierungsauswahl",
    fr: "Choix des alliages",
    pt: "Seleção de ligas"
  },
  val: "316L / 310S / Inconel"
}, {
  label: {
    en: "Cleanability",
    zh: "反吹与清洗性",
    es: "Capacidad de limpieza",
    de: "Reinigbarkeit",
    fr: "Nettoyabilité",
    pt: "Capacidade de limpeza"
  },
  val: "Pulse-Jet Cleanable"
}];
const copy = {
  eyebrowCategory: {
    en: "Gas & Air Filtration",
    zh: "气体与空气过滤",
    es: "Filtración de gas y aire",
    de: "Gas- und Luftfiltration",
    fr: "Filtration des gaz et de l'air",
    pt: "Filtração de gases e ar"
  },
  h1: {
    en: "Gas and Air Filtration",
    zh: "气体与空气过滤丝网",
    es: "Malla de alambre para filtración de gas y aire",
    de: "Drahtgewebe für die Gas- und Luftfiltration",
    fr: "Toile métallique pour la filtration des gaz et de l'air",
    pt: "Malha de arame para filtração de gases e ar"
  },
  lead: {
    en: "Engineered stainless steel and high-nickel alloy woven mesh media designed for high-temperature gas purification, catalyst recovery, hot gas particulate control, and high-velocity gas-liquid coalescing. Fabricated from AISI 316L, 310S heat-resistant stainless steel, and Inconel alloys with apertures from 1 to 250 microns for elevated temperature service.",
    zh: "专为工业高温气体净化、催化剂微粉回收、工艺排气控尘及高速气液聚结设计的耐温耐压不锈钢与特种镍合金编织网介质。选用 AISI 316L、310S 耐热不锈钢以及 Inconel 合金，孔径覆盖 1 至 250 微米，适用于严苛高温工况。",
    es: "Medios filtrantes de malla tejida de acero inoxidable y aleaciones de alto níquel diseñados para purificación de gases a alta temperatura, recuperación de catalizadores, control de partículas en gases calientes y coalescencia gas-líquido a alta velocidad.",
    de: "Präzisionsgefertigte Drahtgewebemedien aus Edelstahl und hoch nickelhaltigen Legierungen für die Hochtemperatur-Gasreinigung, Katalysatorrückgewinnung, Heißgas-Partikelabscheidung und Hochgeschwindigkeits-Gas-Flüssig-Koaleszenz.",
    fr: "Médias filtrants en toile métallique tissée en acier inoxydable et alliages à haute teneur en nickel pour la purification des gaz à haute température, la récupération de catalyseurs, le dépoussiérage de gaz chauds et la coalescence gaz-liquide à grande vitesse.",
    pt: "Meios filtrantes de malha tecida em aço inoxidável e ligas com alto teor de níquel para purificação de gases em alta temperatura, recuperação de catalisadores, controle de particulados em gases quentes e coalescência gás-líquido em alta velocidade."
  },
  ctaQuote: {
    en: "Request a Quote",
    zh: "索取技术报价",
    es: "Solicitar cotización técnica",
    de: "Technisches Angebot anfordern",
    fr: "Demander un devis technique",
    pt: "Solicitar cotação técnica"
  },
  ctaSintered: {
    en: "Sintered Wire Mesh",
    zh: "烧结丝网产品",
    es: "Malla sinterizada",
    de: "Sinterdrahtgewebe",
    fr: "Toile métallique frittée",
    pt: "Malha sinterizada"
  },
  imgAlt: {
    en: "Stainless steel wire mesh filter media for industrial gas and air filtration",
    zh: "工业气体与高温空气过滤不锈钢丝网滤材",
    es: "Medio filtrante de malla de alambre de acero inoxidable para filtración industrial de gas y aire",
    de: "Edelstahldrahtgewebe-Filtermedium für die industrielle Gas- und Luftfiltration",
    fr: "Média filtrant en toile métallique en acier inoxydable pour filtration industrielle de gaz et d'air",
    pt: "Meio filtrante de malha de arame de aço inoxidável para filtração industrial de gases e ar"
  },
  imgBadge: {
    en: "Standard: ASTM E2016",
    zh: "执行标准：ASTM E2016",
    es: "Norma: ASTM E2016",
    de: "Norm: ASTM E2016",
    fr: "Norme : ASTM E2016",
    pt: "Norma: ASTM E2016"
  },
  cardFooterLeft: {
    en: "Pleated, slit coils & sintered tubes",
    zh: "支持分条卷带、折波滤芯与烧结滤管",
    es: "Bobinas cortadas, plisados y tubos sinterizados",
    de: "Plissierte Elemente, Spaltbänder & Sinterrohre",
    fr: "Éléments plissés, bobines refendues et tubes frittés",
    pt: "Bobinas cortadas, plissados e tubos sinterizados"
  },
  cardFooterRight: {
    en: "Sintered Mesh",
    zh: "烧结网",
    es: "Malla sinterizada",
    de: "Sintergewebe",
    fr: "Toile frittée",
    pt: "Malha sinterizada"
  }
};
export default function GasAirHero() {
  const {
    lang
  } = useLanguage();
  return <section id="gasairfiltrationhero" data-nav="light" className="relative bg-background text-foreground mf-hero border-b border-border overflow-hidden">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 cols): Eyebrows, H1, Lead, CTAs */}
          <Reveal className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Eyebrow Badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 mf-caption font-mono tracking-tight text-foreground">
                <Wind className="size-3.5 text-primary" />
                <span>{pick(lang, copy.eyebrowCategory)}</span>
              </Badge>
              <Badge variant="outline" className="rounded-md border border-border bg-muted px-2.5 py-1 mf-caption font-mono text-foreground">
                <ShieldCheck className="mr-1 size-3 text-primary inline" />
                ASTM E2016 / ISO 9044
              </Badge>
            </div>

            {/* Main Headline (Exactly one H1 on the page) */}
            <h1 className="font-default text-balance mf-h1">
              {pick(lang, copy.h1)}
            </h1>

            {/* 2-3 sentence lead paragraph */}
            <p className="mt-4 max-w-2xl mf-lead text-muted-foreground">
              {pick(lang, copy.lead)}
            </p>

            {/* Two CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                <Link to="/SinteredWireMeshPage" className="inline-flex items-center justify-center gap-2">
                  <span>{pick(lang, copy.ctaSintered)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column (5 cols): Gas Mesh Image + Spec Grid Panel */}
          <Reveal delay={60} className="lg:col-span-5">
            <Card className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground gap-0 py-0">
              <div className="mf-card-media relative mf-ratio-hero w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d4dd8f.png" alt={pick(lang, copy.imgAlt)} className="w-full h-full object-cover object-center" loading="eager" fetchPriority="high" decoding="async" />

                <div className="absolute top-3 left-3">
                  <Badge className="bg-card text-foreground border border-border px-2.5 py-1 mf-caption font-semibold rounded-md flex items-center gap-1.5">
                    <SlidersHorizontal className="size-3.5 text-primary" />
                    <span>{pick(lang, copy.imgBadge)}</span>
                  </Badge>
                </div>
              </div>

              <CardContent className="p-4 bg-card">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>
                      {pick(lang, copy.cardFooterLeft)}
                    </span>
                  </div>
                  <Link to="/SinteredMultilayerMeshPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{pick(lang, copy.cardFooterRight)}</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Technical Specs Strip */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">
                {pick(lang, spec.label)}
              </span>
              <span className="mf-spec-strip__value font-mono">
                {spec.val}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}